'use client';

import { useState, useRef } from 'react';
import { useRouter } from '@/i18n/routing';
import { createInsight, updateInsight } from "@/lib/actions/insights";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { ArrowLeft, Save, Upload, X } from "lucide-react";
import { Link } from "@/i18n/routing";

import { RichTextEditor } from "../shared/RichTextEditor";
import { richTextArticleClass, richTextBodyClass } from '@/components/shared/RichText';
import { MediaImg } from '@/components/shared/MediaImg';

interface InsightFormProps {
  initialData?: any;
  allFootprints?: any[];
  serviceCategories?: any[];
}

export function InsightForm({
  initialData,
  allFootprints = [],
  serviceCategories = [],
}: InsightFormProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const router = useRouter();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const isEditing = !!initialData;

  const [formData, setFormData] = useState({
    title: initialData?.title || '',
    slug: initialData?.slug || '',
    categoryId: initialData?.categoryId || '',
    coverImage: initialData?.coverImage || '',
    summary: initialData?.summary || '',
    content: initialData?.content || '',
    isFeatured: initialData?.isFeatured || false,
    author: initialData?.author || '',
    publishDate: initialData?.publishDate 
      ? new Date(initialData.publishDate).toISOString().split('T')[0] 
      : new Date().toISOString().split('T')[0],
    footprintIds: initialData?.footprints ? initialData.footprints.map((f: any) => f.id) : [],
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      const newData = { ...prev, [name]: value };
      if (name === 'title' && !isEditing) {
        newData.slug = value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
      }
      return newData;
    });
  };

  const handleManualChange = (name: string, value: string) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleCategoryChange = (value: string) => {
    setFormData((prev) => ({ ...prev, categoryId: value }));
  };

  const handleSwitchChange = (checked: boolean) => {
    setFormData((prev) => ({ ...prev, isFeatured: checked }));
  };

  const handleFootprintToggle = (id: string) => {
    setFormData((prev) => {
      const currentIds = [...prev.footprintIds];
      const index = currentIds.indexOf(id);
      if (index === -1) {
        currentIds.push(id);
      } else {
        currentIds.splice(index, 1);
      }
      return { ...prev, footprintIds: currentIds };
    });
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }

    setIsUploading(true);
    const uploadData = new FormData();
    uploadData.append('file', file);
    uploadData.append('type', 'insights');

    try {
      const response = await fetch('/api/upload', {
        method: 'POST',
        body: uploadData,
        credentials: 'include',
      });

      const result = await response.json();

      if (result.success) {
        setFormData(prev => ({ ...prev, coverImage: result.url }));
        toast.success('Image uploaded successfully');
      } else {
        toast.error(result.error || 'Upload failed');
      }
    } catch (error) {
      toast.error('An error occurred during upload');
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const removeImage = () => {
    setFormData(prev => ({ ...prev, coverImage: '' }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const result = isEditing 
        ? await updateInsight(initialData.id, formData)
        : await createInsight(formData);

      if (result.success) {
        toast.success(`Article ${isEditing ? 'updated' : 'created'} successfully`);
        router.push('/admin/insights');
      } else {
        toast.error(result.error || "Something went wrong");
      }
    } catch (error) {
      toast.error("Failed to save article");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="outline" size="icon" asChild>
          <Link href="/admin/insights">
            <ArrowLeft className="h-4 w-4" />
          </Link>
        </Button>
        <h1 className="text-3xl font-bold text-navy">
          {isEditing ? 'Edit Article' : 'New Article'}
        </h1>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 pb-20">
          <div className="lg:col-span-2 space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Article Content</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="title">Title</Label>
                  <Input id="title" name="title" value={formData.title} onChange={handleChange} placeholder="Article title..." required />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="slug">Slug (URL)</Label>
                  <Input id="slug" name="slug" value={formData.slug} onChange={handleChange} placeholder="article-slug..." required />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="summary">Summary</Label>
                  <RichTextEditor value={formData.summary} onChange={(val) => handleManualChange('summary', val)} proseClassName={richTextBodyClass} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="content">Content</Label>
                  <RichTextEditor value={formData.content} onChange={(val) => handleManualChange('content', val)} proseClassName={richTextArticleClass} />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Industry Tags (Footprints)</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {allFootprints.map((footprint) => (
                    <label 
                      key={footprint.id} 
                      className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${formData.footprintIds.includes(footprint.id) ? 'bg-primary/5 border-primary shadow-sm' : 'bg-white border-gray-100 hover:bg-gray-50'}`}
                    >
                      <input
                        type="checkbox"
                        className="w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary"
                        checked={formData.footprintIds.includes(footprint.id)}
                        onChange={() => handleFootprintToggle(footprint.id)}
                      />
                      <span className="text-sm font-medium text-navy">{footprint.title}</span>
                    </label>
                  ))}
                  {allFootprints.length === 0 && (
                    <p className="text-sm text-gray-400 py-4 italic">No industry footprints found. Please add some first.</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="lg:col-span-1 space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Article Metadata</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="categoryId">Service Category</Label>
                  <Select value={formData.categoryId} onValueChange={handleCategoryChange}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select service category" />
                    </SelectTrigger>
                    <SelectContent>
                      {serviceCategories.map((cat) => (
                        <SelectItem key={cat.id} value={cat.id}>
                          {cat.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    Categories are managed under Admin → Services → Categories.
                  </p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="author">Author</Label>
                  <Input id="author" name="author" value={formData.author} onChange={handleChange} placeholder="Author name..." />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="publishDate">Publish Date</Label>
                  <Input id="publishDate" name="publishDate" type="date" value={formData.publishDate} onChange={handleChange} required />
                </div>
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border">
                  <div className="space-y-0.5">
                    <Label className="text-sm font-medium">Featured</Label>
                    <p className="text-xs text-gray-500">Show in highlights</p>
                  </div>
                  <Switch checked={formData.isFeatured} onCheckedChange={handleSwitchChange} />
                </div>
                <div className="space-y-4">
                  <Label>Cover Image</Label>
                  {formData.coverImage ? (
                    <div className="relative aspect-video rounded-lg border overflow-hidden group">
                      <MediaImg src={formData.coverImage} alt="Preview" className="w-full h-full object-cover" />
                      <button
                        type="button"
                        onClick={removeImage}
                        className="absolute top-2 right-2 p-1.5 bg-red-600 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ) : (
                    <div 
                      onClick={() => fileInputRef.current?.click()}
                      className="aspect-video rounded-lg border-2 border-dashed border-light flex flex-col items-center justify-center cursor-pointer hover:border-primary hover:bg-light transition-all"
                    >
                      <Upload className="h-8 w-8 text-gray-400 mb-2" />
                      <span className="text-xs text-gray-400 font-medium text-center px-4">Click to upload cover image</span>
                    </div>
                  )}
                  
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileChange}
                    className="hidden"
                    accept="image/*"
                  />
                  
                  {isUploading && (
                    <p className="text-xs text-center text-gray-500 animate-pulse">Uploading...</p>
                  )}
                  
                  <div className="space-y-2">
                    <Label htmlFor="coverImage" className="text-[10px] uppercase">Image Path</Label>
                    <Input 
                      id="coverImage" 
                      name="coverImage" 
                      value={formData.coverImage} 
                      onChange={handleChange} 
                      placeholder="/upload/insights/..." 
                      className="text-xs h-8"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Button type="submit" className="w-full bg-navy hover:bg-primary transition-all font-bold" disabled={isLoading}>
              {isLoading ? 'Publishing...' : <><Save className="mr-2 h-4 w-4" /> Save & Publish</>}
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
}
